import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Trash2, Shield, Loader2, CheckCircle2, Sparkles, LifeBuoy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('superAdminToken') || localStorage.getItem('token')}` });

export default function LaunchCleanup() {
  const [keep, setKeep] = useState('desire,demo');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [done, setDone] = useState(null);
  const [seedingDemo, setSeedingDemo] = useState(false);
  const [demoResult, setDemoResult] = useState(null);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreForm, setRestoreForm] = useState({ slug: '', owner_email: '', owner_name: '', owner_password: '' });
  const [restoring, setRestoring] = useState(false);

  const buildKeepParams = () => {
    return keep.split(',').map(s => s.trim()).filter(Boolean)
      .map(s => `keep_tenants=${encodeURIComponent(s)}`).join('&');
  };

  const handleAuthError = (e) => {
    if (e?.response?.status === 401) {
      toast.error('Your super-admin session is stale. Logging you out — please log back in.');
      try {
        localStorage.removeItem('superAdminToken');
        localStorage.removeItem('token');
      } catch (err) { console.error("./pages/LaunchCleanup.jsx:", err); }
      setTimeout(() => { window.location.href = '/super-admin/login'; }, 1500);
      return true;
    }
    return false;
  };

  const fetchPreview = async () => {
    setLoading(true);
    setDone(null);
    try {
      const r = await axios.post(`${API}/super-admin/cleanup/launch?${buildKeepParams()}`, {}, { headers: getHeaders() });
      setPreview(r.data);
    } catch (e) {
      if (!handleAuthError(e)) toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally { setLoading(false); }
  };

  const seedDemoTenant = async () => {
    if (!window.confirm("This will DROP the existing 'demo-recruitizr' database (if any) and rebuild it with rich demo data: 5 users, 4 companies, 8 positions, 30 candidates, interviews, closures, HRMS records, policies, CRM leads, and a support ticket. Continue?")) return;
    setSeedingDemo(true);
    setDemoResult(null);
    try {
      const r = await axios.post(`${API}/super-admin/cleanup/seed-demo?confirm=true`, {}, { headers: getHeaders() });
      setDemoResult(r.data);
      toast.success('Demo tenant seeded successfully');
      fetchPreview();
    } catch (e) {
      if (!handleAuthError(e)) toast.error(e?.response?.data?.detail || 'Demo seeding failed');
    } finally { setSeedingDemo(false); }
  };

  const restoreTenant = async () => {
    const { slug, owner_email, owner_name, owner_password } = restoreForm;
    if (!slug.trim() || !owner_email.trim() || !owner_password.trim()) {
      toast.error('Slug, owner email and password are required.');
      return;
    }
    setRestoring(true);
    try {
      const qs = new URLSearchParams({
        slug: slug.trim().toLowerCase(),
        owner_email: owner_email.trim(),
        owner_name: owner_name.trim() || 'Owner',
        owner_password: owner_password,
      }).toString();
      const r = await axios.post(`${API}/super-admin/cleanup/restore-tenant?${qs}`, {}, { headers: getHeaders() });
      if (r.data.created) {
        toast.success(`Tenant '${r.data.slug}' restored. Owner can now log in.`);
        setRestoreOpen(false);
        setRestoreForm({ slug: '', owner_email: '', owner_name: '', owner_password: '' });
        fetchPreview();
      } else {
        toast.info(r.data.message || 'Tenant already exists');
      }
    } catch (e) {
      if (!handleAuthError(e)) toast.error(e?.response?.data?.detail || 'Restore failed');
    } finally { setRestoring(false); }
  };

  useEffect(() => { fetchPreview(); /* eslint-disable-next-line */ }, []);

  const runCleanup = async () => {
    if (!preview) return;
    if (preview.missing_keep_slugs?.length) {
      toast.error(`Aborted: must-keep tenant(s) not found: ${preview.missing_keep_slugs.join(', ')}. Adjust the keep list.`);
      return;
    }
    const willDelete = preview.will_delete_tenants?.length || 0;
    if (!window.confirm(
      `This will permanently delete ${willDelete} tenant(s) and clear ${(preview.platform_collections_to_clear || []).length} platform collections. Type the word 'delete' on the next prompt to continue.`
    )) return;
    const word = window.prompt("Type 'delete' to confirm permanent deletion:");
    if ((word || '').trim().toLowerCase() !== 'delete') {
      toast.error('Confirmation phrase not matched. Cleanup cancelled.');
      return;
    }
    setExecuting(true);
    try {
      const r = await axios.post(`${API}/super-admin/cleanup/launch?confirm=true&${buildKeepParams()}`, {}, { headers: getHeaders() });
      setDone(r.data);
      toast.success('Cleanup complete');
      fetchPreview();
    } catch (e) {
      if (!handleAuthError(e)) toast.error(e?.response?.data?.detail || 'Cleanup failed');
    } finally { setExecuting(false); }
  };

  return (
    <div className="space-y-6 text-white" data-testid="launch-cleanup-page">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Launch Cleanup
        </h1>
        <p className="text-slate-400 mt-1">One-time tool to remove all test/dummy data before going live.</p>
      </div>

      <Card className="bg-amber-500/10 border-amber-500/30">
        <CardContent className="p-5 flex gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-200">
            <p className="font-bold mb-1">This is a destructive action.</p>
            <p>It will drop all tenant databases except the ones you list below, and clear test data
              (Demo Requests, Sales CRM, Support Tickets, Audit Logs, Activity Logs, Notifications, etc.)
              on the platform. Plans, CMS content, Super Admin accounts, and branding are preserved.</p>
          </div>
        </CardContent>
      </Card>

      {/* Demo / Restore tools */}
      <Card className="bg-violet-500/5 border-violet-500/30">
        <CardContent className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Sparkles className="h-5 w-5 text-violet-300 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-violet-100">Re-seed Demo Tenant</p>
                <p className="text-xs text-slate-400 mt-1">
                  Wipes and rebuilds <code className="text-violet-300">demo-recruitizr</code> with rich, lived-in data —
                  5 users, 4 client companies, 8 positions, 30 candidates, interviews, closures, HRMS records,
                  policies, CRM leads, and a support ticket. Login stays <code className="text-violet-300">demo@recruitizr.com / demo123</code>.
                </p>
              </div>
            </div>
            <Button onClick={seedDemoTenant} disabled={seedingDemo}
              className="bg-violet-600 hover:bg-violet-500 text-white font-bold w-full"
              data-testid="seed-demo-btn">
              {seedingDemo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {seedingDemo ? 'Seeding...' : 'Re-seed Demo Tenant'}
            </Button>
            {demoResult && (
              <div className="text-xs bg-slate-900/60 border border-slate-700 rounded p-3 space-y-1" data-testid="demo-result">
                <p className="font-bold text-emerald-300">{demoResult.message}</p>
                <p className="text-slate-400">Slug: <span className="text-white">{demoResult.slug}</span></p>
                <p className="text-slate-400">Login: <span className="text-white">{demoResult.login?.email} / {demoResult.login?.password}</span></p>
                <p className="text-slate-400">
                  {Object.entries(demoResult.counts || {}).map(([k, v]) => `${v} ${k}`).join(' · ')}
                </p>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <LifeBuoy className="h-5 w-5 text-cyan-300 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-cyan-100">Restore a Deleted Tenant</p>
                <p className="text-xs text-slate-400 mt-1">
                  Recreate a tenant that was accidentally wiped. Creates a fresh tenant + database with one admin user.
                  Existing tenants with the same slug are left alone.
                </p>
              </div>
            </div>
            {!restoreOpen ? (
              <Button onClick={() => setRestoreOpen(true)} variant="outline"
                className="border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/10 w-full"
                data-testid="restore-tenant-open-btn">
                <LifeBuoy className="w-4 h-4 mr-2" /> Restore Tenant
              </Button>
            ) : (
              <div className="space-y-2">
                <Input placeholder="Slug (e.g. desire)" value={restoreForm.slug}
                  onChange={e => setRestoreForm({ ...restoreForm, slug: e.target.value })}
                  className="bg-slate-800 border-slate-600 text-white text-xs" data-testid="restore-slug" />
                <Input placeholder="Owner display name" value={restoreForm.owner_name}
                  onChange={e => setRestoreForm({ ...restoreForm, owner_name: e.target.value })}
                  className="bg-slate-800 border-slate-600 text-white text-xs" data-testid="restore-name" />
                <Input placeholder="Owner email" value={restoreForm.owner_email}
                  onChange={e => setRestoreForm({ ...restoreForm, owner_email: e.target.value })}
                  className="bg-slate-800 border-slate-600 text-white text-xs" data-testid="restore-email" />
                <Input placeholder="Initial password" type="text" value={restoreForm.owner_password}
                  onChange={e => setRestoreForm({ ...restoreForm, owner_password: e.target.value })}
                  className="bg-slate-800 border-slate-600 text-white text-xs" data-testid="restore-password" />
                <div className="flex gap-2">
                  <Button onClick={restoreTenant} disabled={restoring}
                    className="bg-cyan-600 hover:bg-cyan-500 text-white text-xs flex-1"
                    data-testid="restore-submit-btn">
                    {restoring ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : null}
                    Restore
                  </Button>
                  <Button onClick={() => setRestoreOpen(false)} variant="outline"
                    className="border-slate-600 text-slate-300 text-xs"
                    data-testid="restore-cancel-btn">
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-900 border-slate-700">
        <CardContent className="p-5 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-400 font-bold">
              Tenant slugs to keep (comma-separated)
            </label>
            <Input
              data-testid="keep-tenants-input"
              value={keep}
              onChange={e => setKeep(e.target.value)}
              className="bg-slate-800 border-slate-600 text-white mt-1"
              placeholder="desire,demo"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              These tenants and their databases will be left untouched. All others will be permanently deleted.
            </p>
          </div>
          <Button onClick={fetchPreview} variant="outline"
            className="border-slate-600 text-slate-200 hover:bg-slate-800"
            disabled={loading} data-testid="preview-cleanup-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
            Refresh Preview
          </Button>
        </CardContent>
      </Card>

      {preview && (
        <Card className="bg-slate-900 border-slate-700">
          <CardContent className="p-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-bold text-emerald-400 mb-2">
                  Will keep ({preview.will_keep_tenants?.length || 0})
                </h3>
                <ul className="text-sm space-y-1">
                  {(preview.will_keep_tenants || []).map(t => (
                    <li key={t.slug} className="text-emerald-300">+ {t.slug} <span className="text-slate-500">— {t.name}</span></li>
                  ))}
                  {(preview.will_keep_tenants || []).length === 0 && (
                    <li className="text-rose-400 text-xs">⚠️ No tenants matched — would delete everything!</li>
                  )}
                </ul>
              </div>
              <div>
                <h3 className="text-sm font-bold text-rose-400 mb-2">
                  Will delete ({preview.will_delete_tenants?.length || 0})
                </h3>
                <ul className="text-sm space-y-1 max-h-48 overflow-y-auto pr-2">
                  {(preview.will_delete_tenants || []).map(t => (
                    <li key={t.slug} className="text-rose-300">− {t.slug} <span className="text-slate-500">— {t.name} ({t.db_name})</span></li>
                  ))}
                </ul>
              </div>
            </div>

            <div>
              <h3 className="text-xs font-bold text-slate-400 uppercase mb-2">Platform collections to clear</h3>
              <div className="flex flex-wrap gap-1">
                {(preview.platform_collections_to_clear || []).map(c => (
                  <span key={c} className="text-[11px] px-2 py-0.5 rounded bg-slate-800 text-slate-300">{c}</span>
                ))}
              </div>
            </div>

            {preview.missing_keep_slugs?.length > 0 && (
              <div className="p-3 rounded bg-rose-500/10 border border-rose-500/40 text-sm text-rose-300">
                Safety blocked: tenant slug(s) <strong>{preview.missing_keep_slugs.join(', ')}</strong> are not present in this environment.
                Cleanup will refuse to run until you adjust the keep list.
              </div>
            )}

            <Button onClick={runCleanup}
              disabled={executing || (preview.will_keep_tenants?.length || 0) === 0 || (preview.missing_keep_slugs?.length || 0) > 0}
              className="bg-rose-600 hover:bg-rose-700 text-white font-bold"
              data-testid="execute-cleanup-btn">
              {executing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Running…</> : <><Trash2 className="w-4 h-4 mr-2" />Execute Cleanup</>}
            </Button>
          </CardContent>
        </Card>
      )}

      {done && (
        <Card className="bg-emerald-500/5 border-emerald-500/30">
          <CardContent className="p-5 space-y-2 text-sm">
            <div className="flex items-center gap-2 text-emerald-300 font-bold">
              <CheckCircle2 className="w-5 h-5" />Cleanup complete
            </div>
            <p className="text-slate-300">{done.message}</p>
            <p className="text-slate-400 text-xs">Tenants deleted: <span className="text-white">{done.tenants_deleted}</span></p>
            <p className="text-slate-400 text-xs">Tenants kept: <span className="text-white">{(done.tenants_kept || []).join(', ')}</span></p>
            <p className="text-slate-400 text-xs">DBs dropped: <span className="text-white">{(done.tenant_dbs_dropped || []).length}</span></p>
            <details className="text-xs text-slate-400 mt-2">
              <summary className="cursor-pointer hover:text-slate-200">Collection counts cleared</summary>
              <pre className="mt-2 bg-slate-900 p-2 rounded">{JSON.stringify(done.platform_collections_cleared, null, 2)}</pre>
            </details>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
